import { useEffect, useState } from 'react';
import { TAlert } from '../../types/types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { fontSize, palette } from '../..';
import { getMetaDataDatabase, getMetaDataSchema } from '../../api/dbConnection';
import { NotificationDialog, PopUpSpinner } from '../CommonFunctions/DialogComponents';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {
  getFileDataColumnDetails,
  getMetaDataColumns,
  getMetaDataColumnsForMySQL,
  getMetaDataTable,
  getMetaDataTableForMySQL,
} from '../../api';
import TableName from './TableName';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { ISelectedTable, ITable } from '../../redux/_Dataset/types';
import ShortUniqueId from 'short-unique-id';
import { useDispatch } from 'react-redux';
import {
  RemoveArrow,
  SetSelectedTables,
  UpdateDB,
  UpdateSchema,
} from '../../redux/_Dataset/dataset.action';
import CustomQuery from './CustomQuery';
import { calculateNewNodePosition } from './utils';
interface IDBInfo {
  showFullWidth?: boolean;
  onTableUncheck: (tableIds: string[]) => void;
}
function DBInfo({ onTableUncheck, showFullWidth = true }: IDBInfo) {
  const [databaseList, setDatabaseList] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [tableExpand, setTableExpand] = useState<boolean>(true);
  const [viewExpand, setViewExpand] = useState<boolean>(true);
  const [customQueryExpand, setCustomQueryExpand] = useState(true);
  const [tables, setTables] = useState<ITable[]>([]);
  const [views, setViews] = useState<ITable[]>([]);
  const { selectedConnection, database, schema, selectedTables, canEdit, arrows } = useSelector(
    (state: RootState) => ({
      selectedConnection: state.dataset.connections.find(
        (connection) => connection.id === state.dataset.selectedConnectionId,
      ),
      database: state.dataset.database[state.dataset.selectedConnectionId ?? ''],
      schema: state.dataset.schema[state.dataset.selectedConnectionId ?? ''],
      selectedTables: state.dataset.selectedTables,
      canEdit: state.dataset.canEdit,
      arrows: state.dataset.arrows,
    }),
  );
  const dispatch = useDispatch();
  const uid = new ShortUniqueId({ length: 4, dictionary: 'alpha' });
  useEffect(() => {
    if (!selectedConnection) return;
    setDatabaseList([]);
    setSchemaList([]);
    setTables([]);
    setViews([]);
    const fetchData = async () => {
      setLoading(true);
      if (selectedConnection.fileType === 'flatfile') {
        setLoading(true);

        const found = selectedTables.find((table) => table.flatFileId === selectedConnection.id);

        // ✅ FETCH ONLY IF NOT ALREADY PRESENT
        if (!found) {
          try {
            setShowPopover(true);

            const res = await getFileDataColumnDetails({
              flatFileId: selectedConnection.id,
              workspaceId: '',
            });

            if (res.isError || !res.data) {
              setAlert({
                severity: 'error',
                message: res.message ?? 'Unable to get the columns',
              });
              return;
            }

            const table: ITable = {
              id: uid(),
              table: selectedConnection.label,
              isFlatFile: true,
              connectionId: '',
              connectionName: selectedConnection.label,
              flatFileId: selectedConnection.id,
              isCustomQuery: false,
              customQuery: '',
              database: '',
              schema: '',
              vendor: '',
              isNewTable: true,
              isSelected: true,
            };

            const newTable = {
              ...table,
              alias: table.table,
              tablePositionX: 0,
              tablePositionY: 0,
              columns: res.data.map((column: { fieldName: string; dataType: string }) => ({
                uid: uid(),
                columnName: column.fieldName,
                dataType: column.dataType,
              })),
            };

            // ✅ APPEND — DO NOT OVERWRITE
            const updatedTables = [...selectedTables, newTable];

            dispatch(SetSelectedTables(updatedTables));

            // ✅ SHOW ALL SELECTED FLATFILES
            setTables(updatedTables.filter((t) => t.isFlatFile));
          } finally {
            setShowPopover(false);
          }
        } else {
          // ✅ IF ALREADY PRESENT, STILL SHOW ALL SELECTED
          const flatFiles = selectedTables.filter((t) => t.isFlatFile);
          setTables(flatFiles);
        }

        setViews([]);
        setLoading(false);
        return;
      }

      // SAP HANA → skip DB call, go directly to schema
      if (selectedConnection.vendor === 'saphana') {
        const res = await getMetaDataSchema(
          selectedConnection.id,
          '', // no database for SAP HANA
          selectedConnection?.workSpaceId ?? '',
        );
        if (!res.isError) {
          setSchemaList(res.data);
        } else {
          dispatch(UpdateSchema(selectedConnection.id, ''));
          setAlert({
            severity: 'error',
            message: res.message ?? 'Unable to fetch schema',
          });
        }
        setLoading(false);
        return;
      }

      const res = await getMetaDataDatabase(
        selectedConnection.id,
        selectedConnection?.workSpaceId || '',
      );
      if (!res.isError) {
        setLoading(false);
        setDatabaseList(res.data);
      } else {
        dispatch(UpdateSchema(selectedConnection.id, ''));
        setAlert({
          severity: 'error',
          message: res.message ?? 'Unable to fetch databases',
        });
      }
    };
    fetchData();
  }, [selectedConnection]);
  useEffect(() => {
    if (!selectedConnection || selectedConnection?.fileType === 'flatfile') return;
    if (!database || ['mysql', 'mariadb', 'clickhouse'].includes(selectedConnection.vendor!))
      return;
    if (schema === 'custom query') return; // no need to fetch schemas for custom query

    const fetchData = async () => {
      setLoading(true);
      const res = await getMetaDataSchema(
        selectedConnection?.id,
        database,
        selectedConnection?.workSpaceId ?? '',
      );
      if (!res.isError) {
        setSchemaList(res.data);
      } else {
        dispatch(UpdateSchema(selectedConnection.id, ''));
        setTables([]);
        setViews([]);
        setSchemaList([]);
        setAlert({
          severity: 'error',
          message: res.message ?? 'unable to fetch schema',
        });
      }
      setLoading(false);
    };

    fetchData();
  }, [database, databaseList]);

  /**
   * load tables and views
   *
   */
  useEffect(() => {
    if ((selectedConnection?.vendor !== 'saphana' && !database) || !selectedConnection) return;
    const schemaNeeded = !['mysql', 'mariadb', 'clickhouse'].includes(selectedConnection.vendor!);
    if (!schema && schemaNeeded) return;
    if (schema === 'custom query') return; // no need to fetch tables and views for custom query

    const fetchData = async () => {
      setLoading(true);
      const alreadySelectedOnes = selectedTables
        .filter(
          (table) =>
            table.connectionId === selectedConnection.id &&
            table.database === database &&
            table.schema === schema,
        )
        .map((table) => ({
          id: table.id,
          name: table.table,
          isNew: table.isNewTable,
        }));
      const res = !schemaNeeded
        ? await getMetaDataTableForMySQL({
          databaseId: selectedConnection.id,
          databaseName: database,
          workspaceId: '',
        })
        : await getMetaDataTable({
          databaseId: selectedConnection.id,
          databaseName: database,
          schemaName: schema!,
          workspaceId: '',
        });
      if (res.isError) {
        setTables([]);
        setViews([]);
        setAlert({
          severity: 'error',
          message: res.message ?? 'unable to fetch tables',
        });
      } else {
        const tables = res.data!.tables;
        const views = res.data!.views;

        setTables(
          tables.map((table) => {
            const alreadyexist = alreadySelectedOnes.findIndex((_table) => _table.name === table);
            return {
              id: alreadyexist !== -1 ? alreadySelectedOnes[alreadyexist].id : uid(),
              connectionId: selectedConnection.id,
              isFlatFile: selectedConnection.fileType === 'flatfile',
              flatFileId: selectedConnection.id,
              database,
              schema,
              customQuery: '',
              vendor: selectedConnection.vendor ?? '',
              connectionName: selectedConnection.label,
              isCustomQuery: false,
              isView: false,
              table,
              isNewTable: alreadyexist !== -1 ? alreadySelectedOnes[alreadyexist].isNew : true,
              isSelected: alreadyexist !== -1,
            };
          }),
        );
        setViews(
          views.map((view) => {
            const alreadyexist = alreadySelectedOnes.findIndex((_view) => _view.name === view);
            return {
              id: alreadyexist !== -1 ? alreadySelectedOnes[alreadyexist].id : uid(),
              connectionId: selectedConnection.id,
              isFlatFile: selectedConnection.fileType === 'flatfile',
              flatFileId: selectedConnection.id,
              database,
              schema,
              customQuery: '',
              vendor: selectedConnection.vendor ?? '',
              connectionName: selectedConnection.label,
              isCustomQuery: false,
              isView: true,
              table: view,
              isNewTable: alreadyexist !== -1 ? alreadySelectedOnes[alreadyexist].isNew : true,
              isSelected: alreadyexist !== -1,
            };
          }),
        );
      }
      setLoading(false);
    };
    fetchData();
  }, [database, schema, selectedConnection]);

  useEffect(() => {
    setTables((prevTables) =>
      prevTables.map((table) => {
        const isSelected = selectedTables.some(
          (selected) =>
            selected.table === table.table &&
            selected.connectionId === table.connectionId &&
            selected.database === table.database &&
            selected.schema === table.schema,
          // !selected.isView,
        );
        return { ...table, isSelected };
      }),
    );
    setViews((prevTables) =>
      prevTables.map((table) => {
        const isSelected = selectedTables.some(
          (selected) =>
            // selected.isView &&
            selected.table === table.table &&
            selected.connectionId === table.connectionId &&
            selected.database === table.database &&
            selected.schema === table.schema,
        );
        return { ...table, isSelected };
      }),
    );
  }, [selectedTables]);

  /**
   *
   * @param tableId (id will always be from sidebar it wont contain "_")
   * @returns list of table Ids to be deleted
   */
  const getTableIdsToDelete = (tableId: string): string[] => {
    //for duplicate tables table id will be like this originaltableId_duplicateId
    const target = selectedTables.find((t) => t.id.split('_')[0] === tableId);

    if (!target) return [];

    const duplicates = selectedTables
      .filter((t) => t.table === target.table && t.isDuplicated)
      .map((table) => table.id);

    return duplicates.length > 0
      ? [target.id, ...duplicates] // original + duplicates
      : [target.id]; // only original
  };

  const handleCheckUncheck = async (id: string, checked: boolean) => {
    if (!canEdit || !selectedConnection) return;

    // Find the table (flatfile or DB table)
    const table = [...views, ...tables].find((table) => table.id === id);
    if (!table) return;
    console.log(table)
    if (checked) {
      const schemaNeeded = !['mysql', 'mariadb', 'clickhouse'].includes(selectedConnection.vendor!);
      setShowPopover(true);
      const res = schemaNeeded
        ? await getMetaDataColumns({
          databaseId: selectedConnection.id,
          databaseName: database,
          workspaceId: '',
          tableName: table.table,
          schemaName: schema,
        })
        : await getMetaDataColumnsForMySQL({
          databaseId: selectedConnection.id,
          databaseName: database,
          workspaceId: '',
          tableName: table.table,
        });

      if (res.isError || !res.data) {
        setAlert({ severity: 'error', message: res.message ?? 'Unable to get columns' });
        setShowPopover(false);
        return;
      }

      const columns = res.data.map((column) => ({ uid: uid(), ...column }));
      const position = calculateNewNodePosition(selectedTables.length);
      const newtable: ISelectedTable = {
        ...table,
        tablePositionX: position.x ?? 0,
        tablePositionY: calculateNewNodePosition(selectedTables.length)?.y ?? 0,
        columns: columns,
        alias: table.table,
        isSelected: true,
        order: selectedTables.length + 1,
        isDuplicated: false,
      };
      setShowPopover(false);
      dispatch(SetSelectedTables([...selectedTables, newtable]));
    } else {
      const tableIdsToDelete = getTableIdsToDelete(id);
      if (tableIdsToDelete.length === 0) return;

      //  Remove arrows for ALL affected tables
      const arrowIdsToBeDeleted = [
        ...new Set(
          arrows
            .filter(
              (arrow) =>
                tableIdsToDelete.includes(arrow.startId) || tableIdsToDelete.includes(arrow.endId),
            )
            .map((arrow) => arrow.arrowId),
        ),
      ];
      dispatch(RemoveArrow(arrowIdsToBeDeleted));
      onTableUncheck(tableIdsToDelete);
      dispatch(
        SetSelectedTables(
          selectedTables.filter((table) => !tableIdsToDelete.includes(table.id)),
          //no need to modify order as its handled byu react flow
        ),
      );
      setTables((prevTables) => prevTables.map(table => table.id === id ? {
        ...table, isSelected: false,
        isNewTable : true } : table))
    }
  };

  if (!selectedConnection) return null;
  return (
    <Box
      sx={{
        width: showFullWidth ? '15rem' : '0px',
        height: '100%',
        p: showFullWidth ? 1 : 0,
        pr: 0,
        overflowY: 'auto',
        scrollbarGutter: 'stable both-edges',
      }}
    >
      {selectedConnection.fileType !== 'flatfile' &&
        selectedConnection.vendor?.toLowerCase() !== 'saphana' && (
          <FormControl fullWidth size="small" style={{ marginTop: '5px' }}>
            <InputLabel
              id="dcSelect"
              sx={{
                '&.Mui-focused': {
                  color: `${selectedConnection.color} !important`,
                },
              }}
            >
              Database
            </InputLabel>
            <Select
              sx={{
                fontSize: fontSize.medium,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${selectedConnection.color}`,
                  color: `${selectedConnection.color}`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${selectedConnection.color}`,
                  color: `${selectedConnection.color}`,
                },
                '&.Mui-focused .MuiSvgIcon-root ': {
                  fill: `${selectedConnection.color} !important`,
                },
                '.MuiSelect-select .MuiTypography-root': {
                  fontSize: fontSize.medium,
                },
              }}
              inputProps={{
                sx: {
                  fontSize: fontSize.medium,
                },
              }}
              disabled={!canEdit}
              labelId="dcSelect"
              className="selectBar"
              onChange={(e: any) => {
                dispatch(UpdateDB(selectedConnection.id, e.target.value));
              }}
              // disabled={
              //   (!['mysql', 'mariadb'].includes(serverName) && viewerRestriction) || disableDb
              // }
              value={database}
              key={selectedConnection?.id}
              label="Connection"
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(43, 185, 187, 0.2)',
                      },
                    },
                  },
                },
              }}
            >
              {loading ? (
                <Stack
                  spacing={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                </Stack>
              ) : (
                databaseList &&
                databaseList.map((db: string) => {
                  return (
                    <MenuItem value={db} key={db} title={db}>
                      <Typography
                        sx={{
                          width: 'auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: fontSize.medium,
                        }}
                      >
                        {db}
                      </Typography>
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        )}
      {selectedConnection.fileType !== 'flatfile' &&
        !['mysql', 'mariadb', 'clickhouse'].includes(selectedConnection.vendor!) && (
          <FormControl fullWidth size="small">
            <InputLabel
              id="schemaSelect"
              sx={{
                '&.Mui-focused': {
                  color: `${selectedConnection.color} !important`,
                },
              }}
            >
              Schema
            </InputLabel>
            <Select
              disabled={!canEdit}
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${selectedConnection.color}`,
                  color: `${selectedConnection.color}`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${selectedConnection.color}`,
                  color: `${selectedConnection.color}`,
                },
                '&.Mui-focused .MuiSvgIcon-root ': {
                  fill: `${selectedConnection.color} !important`,
                },
                '.MuiSelect-select .MuiTypography-root': {
                  fontSize: fontSize.medium,
                },
              }}
              key={selectedConnection?.id}
              labelId="schemaSelect"
              className="selectBar"
              label="Schema"
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(43, 185, 187, 0.2)',
                      },
                    },
                  },
                },
              }}
              onChange={(e: any) => dispatch(UpdateSchema(selectedConnection.id, e.target.value))}
              value={schema}
            >
              {loading ? (
                <Stack
                  spacing={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                </Stack>
              ) : schemaList && schemaList.length > 0 ? (
                schemaList.map((schema: string) => {
                  return (
                    <MenuItem value={schema} key={schema}>
                      <Typography
                        sx={{
                          width: 'auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: fontSize.medium,
                        }}
                      >
                        {schema}
                      </Typography>
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem
                  disabled
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: fontSize.medium,
                      textAlign: 'center',
                    }}
                  >
                    No Schemas Available
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '2px',
          marginBottom: '0.5rem',
        }}
      >
        <Typography style={{ fontWeight: 'bold' }}>Tables</Typography>
        <>
          {tableExpand ? (
            <Tooltip
              title="Collapse"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <ArrowDropDownIcon onClick={() => setTableExpand(!tableExpand)} />
            </Tooltip>
          ) : (
            <Tooltip
              title="Expand"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <ArrowRightIcon onClick={() => setTableExpand(!tableExpand)} />
            </Tooltip>
          )}
        </>
      </div>
      {tableExpand ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '5px',
            marginBottom: '1rem',
            textAlign: 'left',
          }}
        >
          {loading ? (
            <Stack
              spacing={1}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
              <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
              <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
            </Stack>
          ) : tables && tables.length > 0 ? (
            tables.map((table, idx: number) => {
              return (
                <TableName
                  table={table}
                  vendor={selectedConnection.vendor ?? ''}
                  key={table.id}
                  onCheckUncheck={handleCheckUncheck}
                  dbColor={selectedConnection.color}
                />
              );
            })
          ) : (
            <div style={{ fontSize: '12px', textAlign: 'center' }}>No Tables Available</div>
          )}
        </div>
      ) : null}
      {selectedConnection.fileType !== 'flatfile' && (
        <>
          <div
            style={{
              display: 'flex',
              borderRadius: '5px',
              textAlign: 'left',
              maxHeight: '330px',
              overflowY: 'auto',
              color: palette.primary.contrastText,
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          >
            <Typography style={{ fontWeight: 'bold' }}>Views</Typography>
            <>
              {viewExpand ? (
                <Tooltip
                  title="Collapse"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <ArrowDropDownIcon onClick={() => setViewExpand(!viewExpand)} />
                </Tooltip>
              ) : (
                <Tooltip
                  title="Expand"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <ArrowRightIcon onClick={() => setViewExpand(!viewExpand)} />
                </Tooltip>
              )}
            </>
          </div>

          {viewExpand ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '5px',
                marginBottom: '1rem',
                textAlign: 'left',
              }}
            >
              {loading ? (
                <Stack
                  spacing={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
                </Stack>
              ) : views && views.length > 0 ? (
                views.map((view) => {
                  return (
                    <TableName
                      table={view}
                      vendor={selectedConnection.vendor ?? ''}
                      key={view.id}
                      onCheckUncheck={handleCheckUncheck}
                      dbColor={selectedConnection.color}
                    />
                  );
                })
              ) : (
                <div style={{ fontSize: '12px', textAlign: 'center' }}>No Views Available</div>
              )}
            </div>
          ) : null}
        </>
      )}
      {selectedConnection.fileType !== 'flatfile' && (
        <>
          <div
            style={{
              display: 'flex',
              borderRadius: '5px',
              marginBottom: '0.5rem',
              textAlign: 'left',
              maxHeight: '330px',
              overflowY: 'auto',
              color: palette.primary.contrastText,
              alignItems: 'center',
            }}
          >
            <Typography style={{ fontWeight: 'bold' }}>Custom Query</Typography>

            <>
              {customQueryExpand ? (
                <Tooltip
                  title="Collapse"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <ArrowDropDownIcon onClick={() => setCustomQueryExpand(!customQueryExpand)} />
                </Tooltip>
              ) : (
                <Tooltip
                  title="Expand"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <ArrowRightIcon onClick={() => setCustomQueryExpand(!customQueryExpand)} />
                </Tooltip>
              )}
            </>
          </div>
          {customQueryExpand ? <CustomQuery /> : null}
        </>
      )}
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}
      <PopUpSpinner
        show={showPopover}
        sx={{ background: 'transparent' }}
        paperProps={{
          sx: {
            backgroundColor: 'transparent',
            color: 'white',
            boxShadow: 'none',
          },
        }}
      />
    </Box>
  );
}
export default DBInfo;
